import { useBlockedIntervals } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/BlockedIntervalsContext";
import { useCalendarAppointments } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarAppointmentsContext";
import { useCalendarGroupSessions } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarGroupSessionsContext";
import { useMemo } from "react";
import useBlockedIntervalsIncludingLiveEdits from "../../../hooks/use-blocked-intervals-including-live-edits";

type ItemWithType =
  | {
      type: "groupSession";
      item: GroupSessionMetaData;
    }
  | {
      type: "appointment";
      item: AppointmentWithSeriesInfo & { invoice?: InvoiceData };
    }
  | {
      type: "gcalEvent";
      item: BlockedInterval;
    }
  | {
      type: "blockedInterval";
      item: BlockedInterval;
    }
  | {
      type: "placeholder";
      item: SessionPlaceholder;
    };

export type NormalizedCalendarEvent = ItemWithType & {
  id: string;
  startISO: string;
  endISO: string;
};

const generateTemporaryId = () => Math.random().toString().substring(2);

const useNormalizedEvents = (includeLiveEdits = false) => {
  const { groupSessions } = useCalendarGroupSessions();
  const { appointments } = useCalendarAppointments();
  const { gcalEvents, blockedIntervals } = useBlockedIntervals();
  const blockedIntervalsIncludingLiveEdits =
    useBlockedIntervalsIncludingLiveEdits();

  const appointmentsForDay: NormalizedCalendarEvent[] = useMemo(
    () =>
      appointments.map((appointment) => ({
        type: "appointment",
        item: appointment,
        startISO: appointment.appointment_start,
        endISO: appointment.appointment_end,
        id: appointment.appointment_id.toString(),
      })),
    [appointments],
  );

  const groupSessionsForDay: NormalizedCalendarEvent[] = useMemo(
    () =>
      groupSessions.map((groupSession) => ({
        type: "groupSession",
        item: groupSession,
        startISO: groupSession.group_session_start,
        endISO: groupSession.group_session_end,
        id: groupSession.group_session_id.toString(),
      })),
    [groupSessions],
  );

  const gcalEventsForDay: NormalizedCalendarEvent[] = useMemo(
    () =>
      gcalEvents.map((gcalEvent) => ({
        type: "gcalEvent",
        item: gcalEvent,
        startISO: gcalEvent.start,
        endISO: gcalEvent.end,
        // TODO: This is a temporary fix for the fact that we don't have a timeslot_id for gcal events
        id: gcalEvent.timeslot_id?.toString() ?? generateTemporaryId(),
      })),
    [gcalEvents],
  );

  const blockedIntervalsForDay: NormalizedCalendarEvent[] = useMemo(() => {
    const blockedIntervalsSource = includeLiveEdits
      ? blockedIntervalsIncludingLiveEdits
      : blockedIntervals;

    return blockedIntervalsSource.map((blockedInterval) => ({
      type: "blockedInterval",
      item: blockedInterval,
      startISO: blockedInterval.start,
      endISO: blockedInterval.end,
      // TODO: This is a temporary fix for the fact that timeslot_id is not required for blocked intervals
      id: blockedInterval.timeslot_id?.toString() ?? generateTemporaryId(),
    }));
  }, [blockedIntervals, blockedIntervalsIncludingLiveEdits, includeLiveEdits]);

  const normalizedEvents = useMemo(() => {
    const allEvents = [
      ...gcalEventsForDay,
      ...blockedIntervalsForDay,
      ...appointmentsForDay,
      ...groupSessionsForDay,
    ];
    return allEvents.sort((a, b) => {
      if (a.startISO < b.startISO) {
        return -1;
      }
      if (a.startISO > b.startISO) {
        return 1;
      }
      return 0;
    });
  }, [
    appointmentsForDay,
    groupSessionsForDay,
    gcalEventsForDay,
    blockedIntervalsForDay,
  ]);

  return normalizedEvents;
};

export default useNormalizedEvents;
